import { Tag, TagProps } from '@chakra-ui/react';
import { LineItemType } from '@pelicargo/types';
import { modelUtils } from '@pelicargo/utils';
import { Backlog, BacklogStatus } from '@prisma/client';

import { API } from '../utils/apiTypes';

type SanitizedBacklog = Pick<
  Backlog,
  'provider_id' | 'airport_id' | 'status' | 'id' | 'starts_at' | 'expires_at' | 'next_flight_at'
>;

type ConfigForTag = {
  isDisplayed: boolean;
  label: string | null;
  tagProps: TagProps;
};
type ConditionForTag = (
  quote: API.QuoteWithTotalCost,
  quoteVariant: API.QuoteWithTotalCost['quote_variants'][0],
  backlogs?: SanitizedBacklog[],
) => ConfigForTag;

const CONDITIONS_FOR_TAGS: ConditionForTag[] = [
  (quote) => ({
    isDisplayed: quote?.is_cheapest,
    label: 'Cheapest',
    tagProps: { colorScheme: 'orange' },
  }),
  (_quote, quoteVariant) => ({
    isDisplayed: quoteVariant?.line_items?.some((node) => node.type === LineItemType.SPOT),
    label: 'Deal',
    tagProps: { colorScheme: 'blue' },
  }),
  (_quote, _quoteVariant, backlogs) => ({
    isDisplayed: !!backlogs?.length,
    label: modelUtils.backlogs.getBacklogStatus(backlogs),
    tagProps: { colorScheme: backlogs?.some((b) => b.status === BacklogStatus.Backlogged) ? 'red' : 'yellow' },
  }),
];

const renderConditionalTag = ({ label, tagProps }: ConfigForTag) => {
  return (
    <Tag key={label} variant="subtle" fontWeight="500" size="md" borderTopRadius={0} {...tagProps}>
      {label}
    </Tag>
  );
};

type Props = {
  quote: API.QuoteWithTotalCost;
  quoteVariant: API.QuoteWithTotalCost['quote_variants'][0];
  backlogs?: SanitizedBacklog[];
};

export const ConditionalTags = ({ quote, quoteVariant, backlogs }: Props) => {
  const matching = CONDITIONS_FOR_TAGS.map((node) => {
    const config = node(quote, quoteVariant, backlogs);
    if (!config.isDisplayed) return null;

    const doesMatch = config.isDisplayed;
    if (doesMatch) return config;
  }).filter(Boolean);

  if (!matching.length) return null;
  return matching.map(renderConditionalTag);
};
